import * as PIXI from "pixi.js";

class WebGLLasers {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.lasers = [];
    this.time = 0;
    this.init();
  }

  init() {
    const colors = [0xff0000, 0x00ff00, 0x0080ff, 0xff00ff, 0xffff00, 0x00ffff];

    // Define potential laser origin positions
    const potentialOrigins = [
      { x: 50, y: 50 }, // Top left corner
      { x: this.app.screen.width - 50, y: 50 }, // Top right corner
      { x: this.app.screen.width / 2, y: 80 }, // Top center
      { x: 50, y: this.app.screen.height - 50 }, // Bottom left
      { x: this.app.screen.width - 50, y: this.app.screen.height - 50 }, // Bottom right
      { x: this.app.screen.width / 4, y: 60 }, // Top left quarter
      { x: (this.app.screen.width * 3) / 4, y: 60 }, // Top right quarter
      { x: this.app.screen.width / 2, y: this.app.screen.height - 50 }, // Bottom center
    ];

    // Randomly select 3-5 origins for this laser show
    const numberOfOrigins = 3 + Math.floor(Math.random() * 3); // 3-5 origins
    const selectedOrigins = [];

    // Shuffle and select random origins
    const shuffledOrigins = [...potentialOrigins].sort(
      () => Math.random() - 0.5
    );
    for (let i = 0; i < numberOfOrigins; i++) {
      selectedOrigins.push(shuffledOrigins[i]);
    }

    selectedOrigins.forEach((origin, originIndex) => {
      const beamsPerOrigin = 3 + Math.floor(Math.random() * 3); // 3-5 beams per origin

      for (let i = 0; i < beamsPerOrigin; i++) {
        const color =
          colors[(originIndex * beamsPerOrigin + i) % colors.length];
        this.createLaser(color, origin, i, beamsPerOrigin);
      }
    });

    this.updateBound = () => this.update();
    this.app.ticker.add(this.updateBound);
  }

  createLaser(color, origin, beamIndex, totalBeams) {
    const laser = {
      graphics: new PIXI.Graphics(),
      color: color,
      originX: origin.x,
      originY: origin.y,
      beamIndex: beamIndex,
      totalBeams: totalBeams,
      angle: Math.random() * Math.PI * 2, // Current angle
      rotationSpeed:
        (Math.random() - 0.5) * 0.025 + (Math.random() > 0.5 ? 0.008 : -0.008),
      beamLength: Math.max(this.app.screen.width, this.app.screen.height) * 1.5,
      intensity: Math.random() * 0.6 + 0.4,
      width: Math.random() * 3 + 2,
      life: 1.0,
      decay: Math.random() * 0.003 + 0.001,
      // Expanding/contracting parameters
      baseAngleOffset: (beamIndex / totalBeams) * Math.PI * 2, // Evenly distribute beams around origin
      expansionPhase: Math.random() * Math.PI * 2,
      expansionSpeed: Math.random() * 0.02 + 0.01,
      minSpread: Math.PI * 0.1, // Minimum beam spread (18 degrees)
      maxSpread: Math.PI * 0.4, // Maximum beam spread (72 degrees)
      // Individual sweep for each beam
      sweepCenter: Math.random() * Math.PI * 2,
      sweepRange: Math.PI * 0.3 + Math.random() * Math.PI * 0.2,
      sweepPhase: Math.random() * Math.PI * 2 + beamIndex * 0.5, // Offset each beam slightly
    };

    this.lasers.push(laser);
    this.container.addChild(laser.graphics);
  }

  update() {
    this.time += 0.016;

    this.lasers.forEach((laser, index) => {
      // Update expansion/contraction phase
      laser.expansionPhase += laser.expansionSpeed;

      // Calculate current spread based on expansion phase
      const expansionFactor = Math.sin(laser.expansionPhase) * 0.5 + 0.5; // 0 to 1
      const currentSpread =
        laser.minSpread + (laser.maxSpread - laser.minSpread) * expansionFactor;

      // Update individual beam sweep
      laser.sweepPhase += laser.rotationSpeed;
      const sweepOffset = Math.sin(laser.sweepPhase) * laser.sweepRange * 0.5;

      // Calculate beam angle: base position + expansion offset + sweep
      const baseAngle = laser.sweepCenter + sweepOffset;
      const spreadOffset = (laser.baseAngleOffset - Math.PI) * currentSpread; // Center around base direction
      laser.angle = baseAngle + spreadOffset;

      // Calculate laser beam endpoint (shooting from origin)
      const endX = laser.originX + Math.cos(laser.angle) * laser.beamLength;
      const endY = laser.originY + Math.sin(laser.angle) * laser.beamLength;

      // Pulsing intensity with flickering
      const pulse = Math.sin(this.time * 6 + index * 1.2) * 0.4 + 0.6;
      const flicker = Math.random() > 0.85 ? Math.random() * 0.4 + 0.6 : 1.0;
      const finalIntensity = laser.intensity * pulse * flicker * laser.life;

      // Life decay and respawn
      laser.life -= laser.decay;
      if (laser.life <= 0) {
        // Respawn with new parameters but keep beam structure
        laser.life = 1.0;
        laser.sweepCenter = Math.random() * Math.PI * 2;
        laser.sweepRange = Math.PI * 0.3 + Math.random() * Math.PI * 0.2;
        laser.rotationSpeed =
          (Math.random() - 0.5) * 0.025 +
          (Math.random() > 0.5 ? 0.008 : -0.008);
        laser.expansionPhase = Math.random() * Math.PI * 2;
        laser.expansionSpeed = Math.random() * 0.02 + 0.01;
        laser.sweepPhase = Math.random() * Math.PI * 2 + laser.beamIndex * 0.5;
      }

      // Clear and redraw laser
      laser.graphics.clear();

      // Draw main laser beam from origin to calculated endpoint
      laser.graphics.moveTo(laser.originX, laser.originY);
      laser.graphics.lineTo(endX, endY);
      laser.graphics.stroke({
        width: laser.width,
        color: laser.color,
        alpha: finalIntensity,
      });

      // Draw outer glow effect
      laser.graphics.moveTo(laser.originX, laser.originY);
      laser.graphics.lineTo(endX, endY);
      laser.graphics.stroke({
        width: laser.width * 3,
        color: laser.color,
        alpha: finalIntensity * 0.3,
      });

      // Add bright white core
      laser.graphics.moveTo(laser.originX, laser.originY);
      laser.graphics.lineTo(endX, endY);
      laser.graphics.stroke({
        width: Math.max(1, laser.width * 0.3),
        color: 0xffffff,
        alpha: finalIntensity * 0.9,
      });

      // Add laser origin point (the "light fixture")
      laser.graphics.circle(laser.originX, laser.originY, 4);
      laser.graphics.fill({
        color: laser.color,
        alpha: finalIntensity * 0.8,
      });

      // Add small bright center to origin
      laser.graphics.circle(laser.originX, laser.originY, 2);
      laser.graphics.fill({
        color: 0xffffff,
        alpha: finalIntensity,
      });

      // Occasionally add atmospheric particles along the beam
      if (Math.random() > 0.92) {
        const t = Math.random() * 0.7 + 0.1; // Mostly near origin
        const particleX = laser.originX + (endX - laser.originX) * t;
        const particleY = laser.originY + (endY - laser.originY) * t;

        laser.graphics.circle(particleX, particleY, 1.5);
        laser.graphics.fill({
          color: laser.color,
          alpha: finalIntensity * 0.7,
        });
      }
    });
  }

  destroy() {
    try {
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      this.lasers = [];

      if (this.container) {
        this.container.destroy({ children: true, texture: true });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLLasers:", e);
    }
  }
}

export default WebGLLasers;
